import {
  useCallback,
  useEffect,
  useRef,
  type MutableRefObject,
  type RefObject,
} from "react";
import { getEditorStoreState } from "../domain/store";
import {
  isTextBlockEditing,
  isTextFrameChromeTarget,
  isInlineMergeTagAutocompleteTarget,
  isTextToolbarTarget,
} from "../domain/text-editing";
import { emailBuilderLabelCommitUpdate } from "../editing/richText/emailBuilderRichTextHtml";
import {
  readEmailBuilderRichTextStoredHtml,
  setEmailBuilderRichTextCaretPoint,
} from "../editing/richText/emailBuilderRichTextSession";

type BlockTarget = {
  rowId: string;
  columnId: string;
  blockId: string;
};

type InlineLabelBlock = {
  id: string;
  text: string;
  fontWeight?: number;
};

type UseEditableButtonEditSessionParams = {
  rowId: string;
  columnId: string;
  block: InlineLabelBlock;
  isEditing: boolean;
  target: BlockTarget;
  setSelection: (selection: { kind: "block" } & BlockTarget) => void;
  setTextEditingBlock: (target: BlockTarget | null) => void;
  updateBlock: (
    rowId: string,
    columnId: string,
    blockId: string,
    patch: Partial<InlineLabelBlock>,
  ) => void;
  setTextEditReentryGuard: (blockId: string | null) => void;
  rootRef: RefObject<HTMLDivElement | null>;
  exitingRef: MutableRefObject<boolean>;
  toolbarPressRef: MutableRefObject<boolean>;
};

function readInlineButtonEditorElement(blockId: string): HTMLElement | null {
  return document.querySelector(`[data-email-builder-inline-button-label="${blockId}"]`);
}

export function useEditableButtonEditSession({
  rowId,
  columnId,
  block,
  isEditing,
  target,
  setSelection,
  setTextEditingBlock,
  updateBlock,
  setTextEditReentryGuard,
  rootRef,
  exitingRef,
  toolbarPressRef,
}: UseEditableButtonEditSessionParams) {
  const exitEdit = useCallback(
    (setGuard: boolean) => {
      const editor = readInlineButtonEditorElement(block.id);
      exitingRef.current = true;
      const nextLabel = readEmailBuilderRichTextStoredHtml(block.id);
      const labelUpdate = nextLabel === null
        ? null
        : emailBuilderLabelCommitUpdate(block.text, block.fontWeight, nextLabel);
      if (labelUpdate !== null) {
        updateBlock(rowId, columnId, block.id, labelUpdate);
      }
      setTextEditingBlock(null);
      if (setGuard) {
        setTextEditReentryGuard(block.id);
      }
      editor?.blur();
      queueMicrotask(() => {
        exitingRef.current = false;
      });
    },
    [
      block.fontWeight,
      block.id,
      block.text,
      columnId,
      exitingRef,
      rowId,
      setTextEditReentryGuard,
      setTextEditingBlock,
      updateBlock,
    ],
  );

  const exitEditRef = useRef(exitEdit);

  useEffect(() => {
    exitEditRef.current = exitEdit;
  }, [exitEdit]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    let cancelled = false;
    let blurTimeoutId = 0;

    const onPointerDown = (event: PointerEvent) => {
      if (exitingRef.current) {
        return;
      }
      const root = rootRef.current;
      const hit = event.target;
      if (!(hit instanceof Node)) {
        return;
      }
      const editor = readInlineButtonEditorElement(block.id);
      if (root?.contains(hit) || editor?.contains(hit)) {
        return;
      }
      if (isTextToolbarTarget(hit) || isInlineMergeTagAutocompleteTarget(hit)) {
        return;
      }
      const frame = rootRef.current?.closest('[data-level="block"]');
      if (isTextFrameChromeTarget(hit, frame)) {
        return;
      }
      exitEditRef.current(true);
    };

    const onEditorBlur = (event: FocusEvent) => {
      if (
        exitingRef.current ||
        isTextToolbarTarget(event.relatedTarget) ||
        isInlineMergeTagAutocompleteTarget(event.relatedTarget)
      ) {
        return;
      }
      if (toolbarPressRef.current) {
        return;
      }
      blurTimeoutId = window.setTimeout(() => {
        if (cancelled || exitingRef.current || toolbarPressRef.current) {
          return;
        }
        const editing = getEditorStoreState().textEditingBlock;
        if (isTextBlockEditing(editing, rowId, columnId, block.id)) {
          exitEditRef.current(true);
        }
      }, 0);
    };

    const onFocusOut = (event: FocusEvent) => {
      const editor = readInlineButtonEditorElement(block.id);
      if (editor === null) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node) || !editor.contains(target)) {
        return;
      }
      const relatedTarget = event.relatedTarget;
      if (relatedTarget instanceof Node && editor.contains(relatedTarget)) {
        return;
      }
      onEditorBlur(event);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusout", onFocusOut, true);

    return () => {
      cancelled = true;
      window.clearTimeout(blurTimeoutId);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusout", onFocusOut, true);
    };
  }, [block.id, columnId, exitingRef, isEditing, rootRef, rowId, toolbarPressRef]);

  function enterEdit(at?: { x: number; y: number }) {
    if (at) {
      setEmailBuilderRichTextCaretPoint(block.id, at);
    }
    setSelection({ kind: "block", ...target });
    setTextEditingBlock(target);
  }

  return { enterEdit };
}
